import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { IDeceasedContact } from '~/types/IDeceasedContact';
import { mq } from '~/lib/mq';

const Wrapper = styled.button`
  background-color: inherit;
  border: none;
  margin: 0;
  width: 100%;
  text-align: left;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:focus,
  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.div`
  font-size: 1.125rem;
  font-weight: bold;

  ${mq('<small')`
    font-size: 1rem;
  `}
`;

const Text = styled.div`
  font-size: 0.875rem;

  ${mq('<small')`
    font-size: 0.75rem;
  `}
`;

interface Props {
  data: IDeceasedContact;
  selectOption: (option: IDeceasedContact) => void;
  selected: boolean;
}

export function Option({ data, selectOption, selected }: Props) {
  const handleClick = useCallback(() => selectOption(data), [
    selectOption,
    data
  ]);

  const subLabel = useMemo(() => {
    const place = data.death?.place || data.birth?.place || data.funeral?.place;
    const birthDate = data.birth?.date ? dayjs(data.birth.date) : null;
    const birthDateFormatted = birthDate?.format('YYYY');
    const deathDate = data.death?.date ? dayjs(data.death.date) : null;
    const deathDateFormatted = deathDate?.format('D MMM YYYY');

    const lifeSpan = [birthDateFormatted, deathDateFormatted]
      .filter((x) => x)
      .join(' - ');

    return [lifeSpan, place].filter((x) => x).join(' • ');
  }, [
    data.birth?.date,
    data.birth?.place,
    data.death?.date,
    data.death?.place,
    data.funeral?.place
  ]);

  return (
    <Wrapper
      role="option"
      type="button"
      data-cy="bitnet-select"
      onClick={handleClick}
      aria-selected={selected}
    >
      <Content>
        <Title>
          {data.firstName} {data.lastName}
        </Title>
        <Text children={subLabel} />
        {data.funeralHome?.name && <Text children={data.funeralHome?.name} />}
      </Content>
    </Wrapper>
  );
}
